import { useState } from 'react';
import { ListChecks, MessageSquareText, Stethoscope, Tv } from 'lucide-react';
import { SplitLayout } from './SplitLayout';
import { Button } from './ui/button';

type ChatTabId = 'chat' | 'state' | 'diagnostic';

function ChatTabBar({
  active,
  onChange,
}: {
  active: ChatTabId;
  onChange: (tab: ChatTabId) => void;
}): React.JSX.Element {
  const tabs: Array<{ id: ChatTabId; label: string; Icon: typeof MessageSquareText }> = [
    { id: 'chat', label: 'Chat', Icon: MessageSquareText },
    { id: 'state', label: 'State', Icon: ListChecks },
    { id: 'diagnostic', label: 'Diagnostic', Icon: Stethoscope },
  ];
  return (
    <div
      role="tablist"
      aria-label="Chat panel"
      className="flex shrink-0 items-center gap-1 border-b border-white/10 px-3 pt-2"
    >
      {tabs.map(({ id, label, Icon }) => {
        const isActive = active === id;
        return (
          /* Tab buttons: rounded-t-md (not pill) — override rounded-full from Button base.
             text-xs = 12px matches the original text-[12px]. */
          <Button
            key={id}
            variant="ghost"
            role="tab"
            aria-selected={isActive}
            data-testid={`chat-tab-${id}`}
            onClick={() => onChange(id)}
            className={`relative rounded-t-md px-3 py-1.5 text-xs font-medium ${
              isActive
                ? // hover:text-crucible-accent overrides ghost's baked hover:text-white
                  // so hovering the ACTIVE tab keeps its accent label.
                  'text-crucible-accent hover:text-crucible-accent'
                : 'text-white/45 hover:text-white/75'
            }`}
          >
            <Icon size={13} strokeWidth={1.75} />
            {label}
            {isActive && (
              <span className="brand-gradient-fill absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-crucible-accent" />
            )}
          </Button>
        );
      })}
    </div>
  );
}

type NorthStarRailMode = 'driver' | 'foundry';

export interface NorthStarRailPanels {
  chatPanel: React.ReactNode;
  statePanel: React.ReactNode;
  diagnosticsPanel: React.ReactNode;
  chatTab: ChatTabId;
  onChatTabChange: (tab: ChatTabId) => void;
}

export function NorthStarRightRail({
  driver,
  chatPanel,
  statePanel,
  diagnosticsPanel,
  chatTab,
  onChatTabChange,
}: NorthStarRailPanels & { driver: React.ReactNode }): React.JSX.Element {
  const [mode, setMode] = useState<NorthStarRailMode>('driver');
  const driverActive = mode === 'driver';
  return (
    <aside
      className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-white/10 bg-[#050409]"
      data-testid="northstar-right-rail"
      data-mode={mode}
      aria-label="North Star command rail"
    >
      <div className="shrink-0 border-b border-white/10 px-3 py-3">
        <div
          className="grid grid-cols-2 rounded-lg border border-white/10 bg-white/[0.04] p-1"
          role="tablist"
          aria-label="North Star command mode"
          data-testid="northstar-right-rail-mode-toggle"
        >
          {/* Mode-toggle buttons: variant="ghost" so the default variant's
              brand-gradient-fill (a background-image rule tailwind-merge cannot
              strip) never leaks onto the inactive segment. Active state layers an
              explicit accent fill on top of ghost; rounded-md (segmented control,
              not pill) overrides the base rounded-full. text-xs = 12px.
              hover:bg-crucible-accent hover:text-black on the ACTIVE branch
              override ghost's baked hover:bg-white/[0.08] hover:text-white so
              hovering the selected segment keeps its accent fill. */}
          <Button
            variant="ghost"
            role="tab"
            aria-selected={driverActive}
            onClick={() => setMode('driver')}
            className={[
              'h-9 rounded-md text-xs font-semibold',
              driverActive
                ? 'bg-crucible-accent text-black hover:bg-crucible-accent hover:text-black'
                : 'text-white/55 hover:bg-white/[0.06] hover:text-white/82',
            ].join(' ')}
            data-testid="northstar-driver-toggle"
          >
            <Tv size={14} strokeWidth={1.8} />
            Driver
          </Button>
          <Button
            variant="ghost"
            role="tab"
            aria-selected={!driverActive}
            onClick={() => setMode('foundry')}
            className={[
              'h-9 rounded-md text-xs font-semibold',
              !driverActive
                ? 'bg-crucible-accent text-black hover:bg-crucible-accent hover:text-black'
                : 'text-white/55 hover:bg-white/[0.06] hover:text-white/82',
            ].join(' ')}
            data-testid="northstar-prosumer-toggle"
          >
            <MessageSquareText size={14} strokeWidth={1.8} />
            Foundry
          </Button>
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-hidden p-3"
        data-testid="northstar-right-rail-driver"
        hidden={!driverActive}
      >
        {driver}
      </div>
      <div
        className="min-h-0 flex-1 overflow-hidden"
        data-testid="northstar-right-rail-foundry"
        hidden={driverActive}
      >
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <ChatTabBar active={chatTab} onChange={onChatTabChange} />
          <div className={chatTab === 'chat' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>
            {chatPanel}
          </div>
          {chatTab === 'state' && (
            <div className="min-h-0 flex-1 overflow-hidden">{statePanel}</div>
          )}
          {chatTab === 'diagnostic' && (
            <div className="min-h-0 flex-1 overflow-hidden">{diagnosticsPanel}</div>
          )}
        </div>
      </div>
    </aside>
  );
}

export interface WorkspaceChatSurfaceProps {
  flagsLoaded: boolean;
  newUi: boolean;
  livePreview: boolean;
  chatPanel: React.ReactNode;
  chatTab: ChatTabId;
  onChatTabChange: (tab: ChatTabId) => void;
  statePanel: React.ReactNode;
  diagnosticsPanel: React.ReactNode;
  previewSurface: React.ReactNode;
  previewSurfaceActive?: boolean;
  previewSurfaceOwnsSecondary?: boolean;
  livePreviewSplitActive: boolean;
  livePreviewChatWidth: number;
  onLivePreviewChatWidthChange: (width: number) => void;
}

/**
 * Renders the Workspace chat tree under a stable live-preview root.
 *
 * Args:
 *   props: Feature flags, chat tab state, and the panels that make up the chat
 *     surface.
 *
 * Returns:
 *   A loading placeholder, the bare chat panel, or the live-preview split shell.
 *
 * Raises:
 *   None.
 */
export function WorkspaceChatSurface(props: WorkspaceChatSurfaceProps): React.JSX.Element {
  if (!props.flagsLoaded) {
    // Do not mount ChatPanel until flags resolve; otherwise live-preview ON
    // swaps the root from bare chat to SplitLayout and remounts the stream.
    // crucible#2990 — the placeholder must be VISIBLY loading, not an empty
    // section: a slow/retrying feature-flag fetch previously read as a
    // silently blank centre column. The 400ms CSS reveal delay keeps the
    // fast path flicker-free.
    return (
      <section
        className="flex flex-1 items-center justify-center overflow-hidden"
        aria-busy="true"
        aria-label="Loading Build panel"
        data-testid="workspace-chat-feature-flags-loading"
      >
        <div className="centre-column-loading-reveal flex flex-col items-center gap-3">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
          <p className="text-sm text-white/55">Loading the Build panel…</p>
        </div>
      </section>
    );
  }

  if (!props.newUi) {
    // Legacy UI still renders ChatPanel unwrapped for pre-split DOM parity.
    return <>{props.chatPanel}</>;
  }

  const previewActive =
    (props.livePreview && props.livePreviewSplitActive) || props.previewSurfaceActive === true;

  if (previewActive && props.previewSurfaceActive === true && props.previewSurfaceOwnsSecondary) {
    return (
      <section
        className="flex min-h-0 flex-1 overflow-hidden"
        data-testid="northstar-consumer-preview-full"
      >
        {props.previewSurface}
      </section>
    );
  }

  // New UI: ChatPanel lives permanently in SplitLayout's secondary slot.
  return (
    <SplitLayout
      secondary={
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <ChatTabBar active={props.chatTab} onChange={props.onChatTabChange} />
          <div className={props.chatTab === 'chat' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>
            {props.chatPanel}
          </div>
          {props.chatTab === 'state' && (
            <div className="min-h-0 flex-1 overflow-hidden">{props.statePanel}</div>
          )}
          {props.chatTab === 'diagnostic' && (
            <div className="min-h-0 flex-1 overflow-hidden">{props.diagnosticsPanel}</div>
          )}
        </div>
      }
      primary={previewActive ? props.previewSurface : null}
      secondaryWidth={props.livePreviewChatWidth}
      onSecondaryWidthChange={props.onLivePreviewChatWidthChange}
      dividerLabel="Resize chat and preview"
      data-testid={
        previewActive
          ? props.previewSurfaceActive === true
            ? 'northstar-consumer-preview-split'
            : 'live-preview-split'
          : undefined
      }
    />
  );
}
